import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/${slug}`);
        if (!alive) return;
        setPage(data);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="custom-page-loading">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="custom-page-not-found">
        <p className="text-lg font-semibold text-slate-900">Page not found</p>
        <p className="text-sm text-slate-500 mt-2">The page you&apos;re looking for doesn&apos;t exist or isn&apos;t published yet.</p>
        <Link to="/" className="mt-6">
          <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-10 px-6 text-sm font-semibold">
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const cleanBody = DOMPurify.sanitize(page.body_html || "");

  return (
    <div data-testid="custom-page" className="page-transition">
      <Seo
        title={page.seo_title || `${page.title} - Antera Software`}
        description={page.seo_description || page.subtitle}
        keywords={page.seo_keywords}
        path={`/p/${page.slug}`}
      />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
              data-testid="custom-page-title"
            >
              {page.title}
            </h1>
            {page.subtitle && (
              <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
                {page.subtitle}
              </p>
            )}
          </motion.div>

          {page.hero_image && (
            <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img
                src={page.hero_image}
                alt={page.title}
                className="w-full h-auto object-cover"
                data-testid="custom-page-hero-image"
              />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      {cleanBody && (
        <section className="pb-16 lg:pb-24 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <article
              data-testid="custom-page-body"
              className="blog-article-content"
              dangerouslySetInnerHTML={{ __html: cleanBody }}
            />
          </div>
        </section>
      )}

      {/* Soft CTA */}
      <section className="bg-slate-50 py-12 lg:py-16 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base text-slate-700">Want to talk to us about {page.title}?</p>
          <Link to="/contact" className="inline-block mt-5">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-7 h-11 text-sm font-semibold">
              Contact us <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
